import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  MenuItem,
  Button,
  Grid,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { isGstApplicable } from './gstSettings';

export default function CreatePackage() {
  const [hotels, setHotels] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [selectedRoomType, setSelectedRoomType] = useState('');
  const [packageName, setPackageName] = useState('');
  const [Fromdate, setFromDate] = useState('');
  const [Todate, setToDate] = useState('');
  const [description, setDescription] = useState('');
  const [weekType, setWeekType] = useState('');
  const [loadingHotels, setLoadingHotels] = useState(true);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const Companycurrency = company?.currency ?? '';

  // ── Company-wide GST switch ─────────────────────────────────────────────
  // OFF = no GST anywhere (room, addon, cover charge, package, bill).
  const [gstOn, setGstOn] = useState(isGstApplicable());
  const handleGstToggle = async (val) => {
    setGstOn(val);
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(
        `${config.API_BASE_URL}/companygst/${company?.id}/${val}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('save failed');
      // Keep sessionStorage in sync so every screen's GST gate updates at once.
      const cd = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
      cd.gstApplicable = val;
      sessionStorage.setItem('companyDetails', JSON.stringify(cd));
      toast.success(`GST ${val ? 'enabled' : 'disabled'} for this company`);
    } catch {
      setGstOn(!val); // revert on failure
      toast.error('Failed to update GST setting');
    }
  };
  const [priceDetails, setPriceDetails] = useState([
    { label: '', basePrice: '' },
  ]);

  // GST Calculation Logic (2025 rules - most common)
  // GST Calculation Logic
  const calculateGST = (basePrice) => {
    if (!basePrice || isNaN(basePrice) || Number(basePrice) <= 0) {
      return { gstPercent: 0, gstAmount: 0, finalPrice: 0 };
    }

    const price = Number(basePrice);
    let gstPercent = 0;

    // Company GST switch OFF → no GST at all. Otherwise apply the slab.
    if (!isGstApplicable()) gstPercent = 0;
    else if (price <= 1000) gstPercent = 0;
    else if (price < 7875)
      // changed here
      gstPercent = 5;
    else gstPercent = 18; // 7875 and above

    const gstAmount = (price * gstPercent) / 100;
    const finalPrice = price + gstAmount;

    return {
      gstPercent,
      gstAmount: gstAmount.toFixed(2),
      finalPrice: finalPrice.toFixed(2),
      basePrice: price.toFixed(2),
    };
  };

  useEffect(() => {
    if (hotels.length > 0 && !selectedHotel) {
      setSelectedHotel(hotels[0].id);
    }
  }, [hotels]);
  const handlePriceChange = (index, field, value) => {
    const updated = [...priceDetails];
    updated[index][field] = value;
    setPriceDetails(updated);
  };

  const addPriceCard = () => {
    setPriceDetails([
      ...priceDetails,
      { label: '', basePrice: '' },
    ]);
  };

  const removePriceCard = (index) => {
    if (priceDetails.length === 1) return;
    const updated = priceDetails.filter((_, i) => i !== index);
    setPriceDetails(updated);
  };

  // Prepare payload for backend - send basePrice (without GST)
  const getTransformedPriceDetails = () => {
    return priceDetails.map((detail) => {
      const { basePrice } = detail;
      const gstInfo = calculateGST(basePrice);

      return {
        adultCount: 0,
        childCount: 0,
        childAge: 0,                 // backend expects int (non-nullable)  send 0
        label: detail.label || '',
        price: Number(gstInfo.basePrice) || 0, // WITHOUT GST
      };
    });
  };

  const handleSubmit = async () => {
    // Basic client-side validation before submit
    if (!packageName.trim()) {
      toast.error('Package name is required!');
      return;
    }
    if (!Fromdate || !Todate) {
      toast.error('Please select both From and To dates!');
      return;
    }
    if (new Date(Fromdate) > new Date(Todate)) {
      toast.error('To Date must be after From Date!');
      return;
    }
    if (!weekType) {
      toast.error('Please select Week Type!');
      return;
    }

    const payload = {
      hotelId: selectedHotel,
      roomTypeId: selectedRoomType,
      packageName,
      description,
      fromDate: Fromdate,
      toDate: Todate,
      weekType,
      priceDetails: getTransformedPriceDetails(),
    };

    console.log('Submitting payload:', JSON.stringify(payload, null, 2));

    const token = sessionStorage.getItem('token');
    if (!token) {
      toast.error('No authentication token found!');
      return;
    }

    try {
      const res = await fetch(`${config.API_BASE_URL}/Home/Create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Failed to create package');

      toast.success('Package created successfully!');
      // Optional: reset form
      setPackageName('');
      setFromDate('');
      setToDate('');
      setDescription('');
      setWeekType('');
      setPriceDetails([
        { label: '', basePrice: '' },
      ]);
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to create package');
    }
  };

  // Fetch Hotels
  useEffect(() => {
    const fetchHotels = async () => {
      const token = sessionStorage.getItem('token');
      if (!token) return;

      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Home/GetHotelsdropdown/${company?.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!res.ok) throw new Error('Failed to load hotels');
        const data = await res.json();
        setHotels(data || []);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load hotels');
      } finally {
        setLoadingHotels(false);
      }
    };
    fetchHotels();
  }, []);

  // Fetch Room Types
  useEffect(() => {
    const fetchRoomTypes = async () => {
      setLoadingRoomTypes(true);
      const token = sessionStorage.getItem('token');
      if (!token) return;

      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Home/GetAllRoomType/${encodeURIComponent(company?.id)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!res.ok) throw new Error('Failed to load room types');
        const data = await res.json();
        setRoomTypes(data || []);
        if (data?.length > 0) {
          setSelectedRoomType(data[0].id);
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load room types');
      } finally {
        setLoadingRoomTypes(false);
      }
    };
    fetchRoomTypes();
  }, []);

  // Date validation useEffect (for real-time feedback)
  useEffect(() => {
    if (Fromdate && Todate && new Date(Fromdate) > new Date(Todate)) {
      toast.warning('To Date should be after From Date');
    }
  }, [Fromdate, Todate]);

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant='h4' gutterBottom sx={{ mb: 4 }}>
        Create New Package
      </Typography>

      {/* GST Setting — company-wide switch */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title='GST Setting' />
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={gstOn}
                onChange={(e) => handleGstToggle(e.target.checked)}
              />
            }
            label={
              gstOn
                ? 'GST Applicable — GST is charged'
                : 'GST OFF — no GST anywhere'
            }
          />
          <Typography variant='body2' color='text.secondary'>
            Turning this OFF removes GST from room booking, addons, cover charge,
            packages and the printed bill across the whole app.
          </Typography>
        </CardContent>
      </Card>

      {/* Hotel Selection */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title='Select Hotel' />
        <CardContent>
          <TextField
            select
            label='Hotel'
            fullWidth
            value={selectedHotel}
            onChange={(e) => setSelectedHotel(e.target.value)}
            disabled={loadingHotels}
            helperText={loadingHotels ? 'Loading hotels...' : ''}>
            {hotels.map((h) => (
              <MenuItem key={h.id || h.ID} value={h.id || h.ID}>
                {h.hotelName || h.HotelName || 'Unnamed Hotel'}
              </MenuItem>
            ))}
          </TextField>
        </CardContent>
      </Card>

      {/* Room Type */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title='Select Room Type' />
        <CardContent>
          <TextField
            select
            label='Room Type'
            fullWidth
            value={selectedRoomType}
            onChange={(e) => setSelectedRoomType(e.target.value)}
            disabled={loadingRoomTypes}>
            {roomTypes.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.roomtype || r.RoomType}
              </MenuItem>
            ))}
          </TextField>
        </CardContent>
      </Card>

      {/* Package Basic Info */}
      <Card
        elevation={3}
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}>
        <CardHeader
          title='Package Information'
          titleTypographyProps={{
            variant: 'h6',
            fontWeight: 600,
            color: 'primary.main',
          }}
          sx={{
            pb: 1,
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        />

        <CardContent sx={{ pt: 3, pb: 4 }}>
          <Grid container spacing={3}>
            {/* Row 1 - Package Name + Dates */}
            <Grid item xs={12} md={6}>
              <TextField
                label='Package Name'
                fullWidth
                required
                value={packageName}
                onChange={(e) => setPackageName(e.target.value)}
                placeholder='e.g. Summer Serenity Package'
                variant='outlined'
                size='medium'
                error={packageName.trim() === ''}
                helperText={
                  packageName.trim() === '' ? 'Package name is required' : ' '
                }
                InputProps={{
                  sx: {
                    bgcolor: 'white',
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 2px rgba(25,118,210,0.2)',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 500 },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label='From Date'
                type='date'
                fullWidth
                required
                value={Fromdate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant='outlined'
                size='medium'
                InputProps={{
                  sx: {
                    bgcolor: 'white',
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 2px rgba(25,118,210,0.2)',
                    },
                  },
                }}
                inputProps={{
                  min: new Date().toISOString().split('T')[0], // today or future
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label='To Date'
                type='date'
                fullWidth
                required
                value={Todate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant='outlined'
                size='medium'
                error={
                  Todate && Fromdate && new Date(Todate) < new Date(Fromdate)
                }
                helperText={
                  Todate && Fromdate && new Date(Todate) < new Date(Fromdate)
                    ? 'To date must be after From date'
                    : ' '
                }
                InputProps={{
                  sx: {
                    bgcolor: 'white',
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 2px rgba(25,118,210,0.2)',
                    },
                  },
                }}
                inputProps={{
                  min: Fromdate || new Date().toISOString().split('T')[0],
                }}
              />
            </Grid>

            {/* Row 2 - Week Type + Description */}
            <Grid container item xs={12} spacing={3} alignItems='flex-start'>
              <Grid item xs={12} sm={5} md={4}>
                <TextField
                  select
                  label='Week Type'
                  fullWidth
                  required
                  value={weekType}
                  onChange={(e) => setWeekType(e.target.value)}
                  variant='outlined'
                  size='medium'
                  helperText={weekType ? ' ' : 'Please select week type'}
                  error={!weekType}
                  InputProps={{
                    sx: {
                      bgcolor: 'white',
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 2px rgba(25,118,210,0.2)',
                      },
                    },
                  }}>
                  <MenuItem value='' disabled>
                    Select week type
                  </MenuItem>
                  <MenuItem value='Weekday'>Weekday</MenuItem>
                  <MenuItem value='Weekend'>Weekend</MenuItem>
                  <MenuItem value='All Week'>All Week</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={7} md={8}>
                <TextField
                  label='Description'
                  fullWidth
                  multiline
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder='Package highlights, inclusions, special terms, validity period...'
                  variant='outlined'
                  size='medium'
                  InputProps={{
                    sx: {
                      bgcolor: 'white',
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 2px rgba(25,118,210,0.2)',
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: { fontWeight: 500 },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Price Details */}
      <Typography variant='h5' gutterBottom sx={{ mt: 4, mb: 2 }}>
        Price Details (Enter price without GST)
      </Typography>

      {priceDetails.map((detail, index) => {
        const gstInfo = calculateGST(detail.basePrice);

        return (
          <Card key={index} sx={{ mb: 3, position: 'relative' }}>
            <CardContent>
              <Grid container spacing={2} alignItems='flex-end'>
                <Grid item xs={12} sm={7}>
                  <TextField
                    label='Label (optional)'
                    fullWidth
                    value={detail.label || ''}
                    onChange={(e) =>
                      handlePriceChange(index, 'label', e.target.value)
                    }
                    placeholder='e.g. Standard, Peak, Weekend'
                  />
                </Grid>

                <Grid item xs={12} sm={5}>
                  <TextField
                    label='Base Price (without GST)'
                    type='number'
                    fullWidth
                    value={detail.basePrice}
                    onChange={(e) =>
                      handlePriceChange(index, 'basePrice', e.target.value)
                    }
                    placeholder='Enter amount'
                  />
                </Grid>

                {/* GST Information - Read Only */}
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {gstOn && (
                    <Grid item xs={4}>
                      <TextField
                        label='GST %'
                        fullWidth
                        size='small'
                        value={`${gstInfo.gstPercent}%`}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    )}
                    {gstOn && (
                    <Grid item xs={4}>
                      <TextField
                        label='GST Amount'
                        fullWidth
                        size='small'
                        value={
                          gstInfo.gstAmount
                            ? `${Companycurrency}${gstInfo.gstAmount}`
                            : '-'
                        }
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    )}
                    <Grid item xs={gstOn ? 4 : 12}>
                      <TextField
                        label={gstOn ? 'Final Price (incl. GST)' : 'Final Price'}
                        fullWidth
                        size='small'
                        value={
                          gstInfo.finalPrice
                            ? `${Companycurrency}${gstInfo.finalPrice}`
                            : '-'
                        }
                        InputProps={{ readOnly: true }}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {priceDetails.length > 1 && (
                  <IconButton
                    color='error'
                    onClick={() => removePriceCard(index)}
                    sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Grid>
            </CardContent>
          </Card>
        );
      })}

      <Grid container justifyContent='space-between' sx={{ mt: 4 }}>
        <Button variant='outlined' onClick={addPriceCard}>
          + Add Another Price Slab
        </Button>

        <Button
          variant='contained'
          color='success'
          size='large'
          onClick={handleSubmit}>
          Save Package
        </Button>
      </Grid>
    </div>
  );
}
